<template>
  <div class="main-page-container">
    <PageHeader :title="$t('kioskLogs.title')"/>
    <section id="kioskGrid" class="kiosk-logs-white-bg-sec">
      <DxDataGrid
        id="kioskLogGridContainer"
        key-expr="id"
        :ref="kioskLogGridRef"
        :data-source="dataSource"
        :show-borders="false"
        :remote-operations="false"
        :showColumnLines="true"
        :showRowLines="true"
        :columnAutoWidth="true"
        :customizeExportData="customizeColumns"
        :allowColumnReordering="true"
        :allowColumnResizing="true"
        :rowAlternationEnabled="false"
        :repaint-changes-only="true"
        :two-way-binding-enabled="true"
        @editor-preparing="onEditorPreparing($event)"
        @content-ready="contentReady($event)"
        @data-error-occurred="onDataErrorOccurred($event)"
        :rtl-enabled="rtlEnabled"
        :no-data-text="$t('common.grid.noDataYet')"
      >
        <DxScrolling mode="standard" :use-native="!rtlEnabled"/>
        <DxLoadPanel :enabled="true"> </DxLoadPanel>

        <!-- start:: Kiosk Name -->
        <DxColumn :caption="$t('kioskLogs.kioskName')" :filter-operations="allowedOperations" data-field="kiosk_name" />
        <!-- end:: Kiosk Name -->

        <!-- start:: Requested by -->
        <DxColumn :filter-operations="allowedOperations"
          :caption="$t('kioskLogs.requestedBy')"
          data-field="user_name"
          :calculate-cell-value="addPrefixForUserName"
        />
        <!-- end:: Requested by -->

        <!-- start:: Date -->
        <DxColumn :filter-operations="allowedOperations"
          :caption="$t('kioskLogs.date')"
          data-field="created_at"
          :calculate-cell-value="formatDate"
        />
        <!-- end:: Date -->

        <!-- start:: Type -->
        <DxColumn data-field="type" :caption="$t('kioskLogs.type')" :calculate-cell-value="typeCustomizeText" :filter-operations="allowedOperations" />
        <!-- end:: Type -->

        <!-- start:: Status -->
        <DxColumn :filter-operations="allowedOperations"
          data-field="status"
          :caption="$t('common.status')"
          :customize-text="statusCustomizeText"
          cell-template="status-cell"
        />

        <template #status-cell="{data}">
          <span
            :class="{
              'success-style': data.data.status == 'succeeded',
              'failed-style': data.data.status == 'failed',
              'other-status-style':
                data.data.status != 'failed' && data.data.status != 'succeeded',
            }"
          >
          {{ kioskStatus[data.data.status] }}
            <!-- {{
              data.data.status.charAt(0).toUpperCase() +
                data.data.status.slice(1)
            }} -->
          </span>
        </template>
        <!-- end:: Status -->

        <!-- start:: Reason of failure -->
        <DxColumn
          data-field="reason_of_failure"
          :caption="$t('kioskLogs.reasonOfFailure')"
          :allow-header-filtering="true"
          :allow-filtering="false"
          :calculate-cell-value="displayNA"
        >
        </DxColumn>
        <!-- end:: Reason of failure -->

        <!-- start:: Action -->
        <DxColumn
          :min-width="150"
          alignment="center"
          css-class=""
          :caption="$t('common.grid.actions')"
          :width="200"
          :allow-filtering="false"
          :allow-exporting="false"
          :allow-editing="false"
          data-field="downloadable_file"
          :visible="true"
          cell-template="action-cell"
          :show-in-column-chooser="false"
        />
        <template #action-cell="{ data }">
          <a
            v-if="
            data.data.downloadable_file && ( 
              data.data.type == 'remote_show_screens' || 
              data.data.type == 'manual_pull_database' || 
              data.data.type == 'kiosk_logging'
              )"
            :href="`${data.data.downloadable_file}`"
            target="_blank"
            download
            class="download-export-btn"
            style="color: white"
            >{{ $t('common.download') }}</a
          >
          <template v-else>-</template>
        </template>
        <!-- end:: Action -->

        <!-- start:: Grid filters -->
        <DxFilterRow :visible="true"/>
        <DxHeaderFilter :visible="true" />
        <!-- end:: Grid filters -->

        <!-- start:: Toolbar -->
        <DxToolbar>
          <!-- <DxItem name="groupPanel"/> -->
          <DxItem location="before" template="columnButtonTemplate" />
          <DxItem template="exportButtonTemplate" location="before" />
          <DxItem template="clearAllTemplate" location="before" />
        </DxToolbar>

        <!-- start::Toolbar templates -->
        <template #exportButtonTemplate>
          <DxDropDownButton
            class="export-dropdown"
            :items="buttonData"
            :text="$t('common.grid.export')"
            @item-click="onItemClick"
          />
        </template>

        <template #columnButtonTemplate>
          <button
            class="custom-columns"
            @click="dataGrid.showColumnChooser(), positionColumnChooser()"
          >
            <span class="custom-text">{{ $t('common.grid.columns') }} </span>

            <span class="custom-icon">            
              <i class="dx-icon dx-icon-spindown dx-icon-right"></i>
            </span>
          </button>
        </template>

        <template #clearAllTemplate>
          <div v-if="showClearAll" class="clear-cols d-flex align-items-center">
            <div class="toolbar-custom-line"></div>
            <Button
            :text="$t('common.grid.clearFilters')"
              @click="reset()"
              styling-mode="outlined"
              type="danger"
              class="clear-CustomColums"
            />
          </div>
        </template>

        <!-- start::Toolbar templates -->

        <!-- end:: Toolbar -->

        <!-- start:: Pager -->
        <DxPager
          :visible="true"
          :allowed-page-sizes="pageSizes"
          :show-page-size-selector="true"
          :show-navigation-buttons="true"
          :display-mode="'full'"
          :show-info="true"
          :infoText="rtlEnabled ?  'صفحة {0} من {1} ({2} إجمالي العناصر)' : ''"
        />
        <!-- end:: Pager -->

        <!-- start::Column Chooser -->
        <DxColumnChooser :enabled="true" mode="select" :title="$t('common.grid.columnChooser')"/>
        <!-- start::Column Chooser -->

        <DxSearchPanel :visible="false" />

        <!-- start:: Pager -->
        <DxPaging :page-size="50" />
        <!-- end:: Pager -->
      </DxDataGrid>
    </section>
  </div>
</template>
<script>
import {
  DxDataGrid,
  DxPager,
  DxPaging,
  DxColumn,
  DxSearchPanel,
  DxItem,
  DxToolbar,
  DxColumnChooser,
  DxHeaderFilter,
  DxFilterRow,
  DxLoadPanel,
  DxScrolling,
} from "devextreme-vue/data-grid";
import moment from "moment";
import PageHeader from "@/components/page-header";
import DxDropDownButton from "devextreme-vue/drop-down-button";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver-es";
import { exportDataGrid } from "devextreme/excel_exporter";
import { exportDataGrid as exportDataGridToPdf } from "devextreme/pdf_exporter";
import "jspdf-autotable";
import { mapGetters, mapActions } from "vuex";
import { jsPDF } from "jspdf";
import { default as Button } from "devextreme-vue/button";
import amiriFont from "@/assets/fonts/Amiri-Regular.ttf"; // Load the Cairo font

const kioskLogGridRef = "kioskLogGridContainer";

export default {
  title: "Kiosk Logs",
  components: {
    DxDataGrid,
    DxColumn,
    DxPager,
    DxPaging,
    DxSearchPanel,
    PageHeader,
    DxDropDownButton,
    DxToolbar,
    DxItem,
    DxColumnChooser,
    DxFilterRow,
    DxHeaderFilter,
    DxLoadPanel,
    Button,
    DxScrolling,
  },
  data() {
    return {
      rofHeaderFilterDataSrouce: [],
      pageSizes: [5, 10, 50],
      buttonData: ["Excel", "CSV", "PDF"],
      kioskLogGridRef,
      requestTypesMapper: {
        update_app: this.$t('sitesAndDevices.kioskActions.updateApp'),
        push_restart: this.$t('sitesAndDevices.kioskActions.restartKiosk'),
        restart_app: this.$t('sitesAndDevices.kioskActions.restartApp'),
        kiosk_logging: this.$t('sitesAndDevices.kioskActions.getLogs'),
        force_resync_with_logs: this.$t('sitesAndDevices.kioskActions.previewLogs'),
        remote_show_screens: this.$t('sitesAndDevices.kioskActions.screenshot'),
        manual_pull_database: this.$t('sitesAndDevices.kioskActions.getDatabase'),
        open_door: this.$t('sitesAndDevices.kioskActions.openDoor'),
        refresh_face_library: this.$t('sitesAndDevices.kioskActions.refreshFaceLibrary'),
        screen_projection: this.$t('sitesAndDevices.kioskActions.remoteControl'),
      },
      kioskStatus: {
        placed: this.$t("sitesAndDevices.kioskStatus.placed"),
        pending: this.$t("sitesAndDevices.kioskStatus.pending"),
        delivered: this.$t("sitesAndDevices.kioskStatus.delivered"),
        succeeded: this.$t("sitesAndDevices.kioskStatus.succeeded"),
        failed: this.$t("sitesAndDevices.kioskStatus.failed"),
        running: this.$t("sitesAndDevices.kioskStatus.running"),
        canceled: this.$t("sitesAndDevices.kioskStatus.canceled"),
        closed: this.$t("sitesAndDevices.kioskStatus.closed"),
      },
      allowedOperations: ["contains"],
      showClearAll : false,
      rtlEnabled : false,
    };
  },
  props: ["kiosk_mac_address"],
  methods: {
    ...mapActions("daemon", ["fetchRequestLogs", "fetchKioskRequestLogs"]),
    contentReady(e) {
        let sortedExp = this.getSortedColumns();
        let filterExp = e.component.getCombinedFilter();
        if(filterExp || sortedExp) {
            this.showClearAll = true;
        }else{
            this.showClearAll = false;
        }
    },
    getSortedColumns() {
        let allColumns = Array.from(Array(this.dataGrid.columnCount()).keys()).map(index => this.dataGrid.columnOption(index));
        allColumns = allColumns.find(col => col.sortIndex != null);
        return allColumns;
    },
    positionColumnChooser() {
      const columnChooserView = this.dataGrid.getView("columnChooserView");

      if (!columnChooserView._popupContainer) {
        columnChooserView._initializePopupContainer();
        columnChooserView.render();
      }

      columnChooserView._popupContainer.option("position", {
        of: this.dataGrid.element(),
        my: this.rtlEnabled ? "right top" : "left top",
        at: this.rtlEnabled ? "right top" : "left top",
        offset: "0 50",
      });
    },
    onItemClick(e) {
      e.rtlEnabled = this.rtlEnabled;
      if (e.itemData == "Excel") this.exportXLSX();
      else if (e.itemData == "CSV") this.exportCSV();
      else if (e.itemData == "PDF") this.exportPDF(e);
    },
    exportXLSX() {
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet("Main sheet");
      exportDataGrid({
        component: this.dataGrid,
        worksheet,
        autoFilterEnabled: true,
      }).then(() => {
        workbook.xlsx.writeBuffer().then((buffer) => {
          saveAs(
            new Blob([buffer], { type: "application/octet-stream" }),
            "Kiosk Logs.xlsx"
          );
          this.isExport = false;
        });
      });
    },
    exportCSV() {
      this.dataGrid.beginUpdate();
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet("Main sheet");

      exportDataGrid({
        component: this.dataGrid,
        worksheet: worksheet,
      }).then(() => {
        workbook.csv.writeBuffer().then((buffer) => {
          saveAs(
            new Blob([buffer], { type: "application/octet-stream" }),
            "Kiosk Logs.csv"
          );
          this.dataGrid.endUpdate();
        });
      });
    },
    exportPDF(e) {

      const doc = new jsPDF('landscape');
        
      // Add custom font
      doc.addFont(amiriFont, "Amiri", "normal");
      doc.setFont("Amiri");

      doc.autoTable({ direction: "rtl" });

      exportDataGridToPdf({
        jsPDFDocument: doc,
        component: this.dataGrid,
        customizeCell: function (options) {
          const { gridCell, pdfCell } = options;

          if (gridCell.rowType === "data") {
            // data columns style
            pdfCell.styles = {
              font: e.rtlEnabled ? "Amiri" : "Lato",
              fontStyle: "normal",
              fontSize: 8,
              cellWidth: "auto",
              halign: "center",
            };
          } else {
            // header columns style
            pdfCell.styles = {
              font: e.rtlEnabled ? "Amiri" : "Lato",
              fontStyle: "normal",
              fontSize: 8,
              fillColor: "#2680EB",
              textColor: "#FFFFFF",
              cellWidth: "auto",
              halign: "center", 
            };
          }
        },
        customizeTable: (doc) => {
          doc.autoTable({ direction: "rtl" }); // Ensure table follows RTL direction
        },
      }).then(() => {
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i);
          const pageSize = doc.internal.pageSize;
          const pageWidth = pageSize.width
            ? pageSize.width
            : pageSize.getWidth();
          const pageHeight = pageSize.height
            ? pageSize.height
            : pageSize.getHeight();
          const header = this.$t('kioskLogs.title');
          const headerWidth = doc.getTextDimensions(header).w;
          const footer = `${this.$t("common.page")} ${i} ${this.$t("common.of")} ${pageCount}`;

          // Header
          doc.text(header, (pageWidth - headerWidth) / 2, 10);

          // Footer
          doc.text(
            footer,
            pageWidth / 2 - doc.getTextWidth(footer) / 2,
            pageHeight - 5,
            { baseline: "bottom" }
          );
        }
        doc.save("Kiosk Logs.pdf");
      });
    },
    reset() {
      this.dataGrid.clearFilter("row");
      this.dataGrid.clearFilter("header");
      this.dataGrid.clearSorting();     
      this.showClearAll = false;
    },
    customizeColumns(columns) {
      columns.forEach((col) => {
        if (col.caption == "Actions") {
          col.width = 200;
          col.visibleWidth = 100;
          console.log(col);
        }
      });
    },
    onEditorPreparing(e) {
      if (e.parentType == "filterRow") {
        e.editorOptions.placeholder = this.$t("common.grid.search");
      }
    },
    formatDate(e) {
      let date 
    if (this.getLanguage == 'ar') 
      date = moment(
        e.updated_at
      ).locale(this.getLanguage == 'ar' ?"ar_SA":"en").format("yyyy/MM/DD hh:mm A");
    else
      date = moment(e.updated_at).format("DD/MM/yyyy hh:mm A");

      return date;
    },
    statusCustomizeText(e) {
      return this.kioskStatus[e.valueText]
      // return e.valueText.charAt(0).toUpperCase() + e.valueText.slice(1);
    },
    addPrefixForUserName(e) {
      // let userName;
      if (e.user_name) {
        return e.user_name.toLowerCase() == "default user"
          ? "Admin - CS"
          : `Admin - ${e.user_name}`;
      }
      return "";

      // console.log(e.user_name.toLowerCase())
      // return userName;
    },
    typeCustomizeText(e) {
      return this.requestTypesMapper[e.type];
    },
    displayNA(e) {
     let msg = ""
      if(e.reason_of_failure.includes("Cannot find the kiosk's connection"))
        msg = this.$t('sitesAndDevices.reasonOfFailure.connection')
      else if (e.reason_of_failure.includes("Cannot find the running request"))
        msg = this.$t('sitesAndDevices.reasonOfFailure.request')
      else
        msg = e.reason_of_failure
      return e.reason_of_failure ? msg : "-";
    },
    onDataErrorOccurred(e){
      console.log('onDataErrorOccurred e', e);
      setTimeout(() => {  
        let errorRow = document.querySelector(".dx-datagrid-headers .dx-error-message");  
        errorRow.innerHTML = this.$t('common.notes.anErrorOccurredGrid');  
      },100);
    },
  },
  mounted() {
    if (this.getLanguage == 'ar') {
      this.rtlEnabled = true;
    }
    if (this.kiosk_mac_address) {
      this.fetchKioskRequestLogs(this.kiosk_mac_address);
    } else {
      this.fetchRequestLogs();
    }

    //Assign rofHeaderFilterDataSrouce with header values to avoid
    //blanks from showing in header filter

    setTimeout(
      this.dataSource.forEach((log) => {
        const reasonOfFailure = log.reason_of_failure;
        const isFound = this.rofHeaderFilterDataSrouce.find(
          (elem) => elem.value == reasonOfFailure
        );
        if (!isFound && reasonOfFailure) {
          this.rofHeaderFilterDataSrouce.push({
            value: reasonOfFailure,
            text: reasonOfFailure,
          });
        }
      }),
      600
    );

    // console.log(this.rofHeaderFilterDataSrouce);

    //Check logs after fetching either (entity kiosks or single kiosk)
    // console.log(this.getRequestLogs);
  },
  computed: {
    ...mapGetters("i18N",["getLanguage"]),
    ...mapGetters("daemon", ["getRequestLogs", "getLoading"]),
    dataSource() {
      return this.getRequestLogs;
    },
    dataGrid() {
      return this.$refs[this.kioskLogGridRef].instance;
    },
  },
  created(){
    document.addEventListener("mousedown", (e) => {
      if (e.path) {
        let isColumnChooser = false;
        for (let i = 0; i < e.path.length; i++) {
        if (typeof e.path[i].matches == "function") {
                if (e.path[i].matches(".dx-overlay-content")) {
                    isColumnChooser = true;
                }
            }
        }
        if (!isColumnChooser) this.dataGrid.hideColumnChooser();
      }
    });
  },
};
</script>

<style lang="scss">
div#kioskLogGridContainer {
  td.dx-group-cell {
    font-weight: 700 !important;
    font-size: 16px !important;
    line-height: 19px !important;

    color: #495057 !important;
  }
  .dx-widget.dx-datagrid-pager.dx-pager {
    padding: 13px 40px !important;
  }
  .dx-toolbar-after {
    display: none;
  }
  .dx-datagrid .dx-sort-down::before {
    content: "\f052";
    color: #939393;
    font-size: 16px;
    font-weight: 700;
  }
  .dx-datagrid .dx-header-filter::before {
    content: "\f050";
    color: #939393;
    font-weight: 700;
  }
  .dx-datagrid-group-opened::before {
    content: "\f016";
    color: #636161;
    font-size: 16px;
    font-weight: 700;
  }
  .dx-datagrid .dx-sort-up::before {
    font-family: DXIcons, sans-serif;
    color: #939393;
    font-size: 16px !important;
    font-weight: 700 !important;
  }
  .dx-datagrid-content
    .dx-datagrid-table
    [class*="column"]
    + [class*="column"]:last-child {
    height: 37px;
  }
  .custom-row-style {
    background: #ffffff;
    font-weight: 400;
    font-size: 16px;
    line-height: 19px;
    color: #495057;
    .bold-name.dx-template-wrapper {
      font-weight: 700 !important;
      font-size: 16px !important;
      line-height: 19px !important;
      text-decoration-line: none;
      color: #495057 !important;
    }
  }
  .dx-datagrid-rowsview .dx-row.dx-group-row {

    font-weight: 700 !important;
    font-size: 16px !important;
    line-height: 19px !important;

    color: #495057 !important;
  }
  td.dx-cell-focus-disabled.dx-master-detail-cell {
    background: white !important;
    padding-inline-start: 0 !important;
  }
  tbody {
    color: #495057;
  }
}

.action-button {
  background: #103f5e !important;
  color: white;
  border-radius: 21px;
  width: 109px;
  height: 27px;
  display: flex;
  align-items: center;
  font-weight: 400;
  font-size: 16px;
  line-height: 19px;
  pointer-events: none;
}

.success-style {
  color: #34c38f;
  font-weight: 700;
  font-size: 16px !important;
  line-height: 19px !important;
}

.failed-style {
  color: #f46a6a;
  font-weight: 700;
  font-size: 16px !important;
  line-height: 19px !important;
}

.other-status-style {
  color: #ffa500;
  font-weight: 700;
  font-size: 16px !important;
  line-height: 19px !important;
}

</style>
<style lang="scss" scoped>
.kiosk-logs-white-bg-sec {
        background-color: white;
        padding-inline-start: 0px;
        // padding-top: 25px;
        border-radius: 8px!important;
    }
</style>